import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Users, Check, X, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";

interface InvitationDetails {
  id: string;
  email: string;  // Add email field
  team_name: string;
  inviter_name: string;
  inviter_email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export default function TeamInvitation() {
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(useSearch());
  const token = searchParams.get('token');
  const { toast } = useToast();
  const { user, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [expired, setExpired] = useState(false);
  const [wrongUser, setWrongUser] = useState(false);

  useEffect(() => {
    if (!token) {
      toast({
        title: "Error",
        description: "Invalid invitation link",
        variant: "destructive",
      });
      setLocation('/');
      return;
    }
    
    verifyInvitation();
  }, [token]);
  
  // Check for wrong user whenever user or invitation changes
  useEffect(() => {
    if (user && invitation && user.email !== invitation.email) {
      setWrongUser(true);
    } else {
      setWrongUser(false);
    }
  }, [user, invitation]);

  const verifyInvitation = async () => {
    try {
      const response = await api.get(`/api/teams/invitations/verify/${token}`);
      setInvitation(response.data);
      
      // Check if expired
      const expiryDate = new Date(response.data.expires_at);
      if (expiryDate < new Date()) {
        setExpired(true);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Invalid or expired invitation',
        variant: "destructive",
      });
      setLocation('/');
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!token) return;
    
    setAccepting(true);
    try {
      await api.post(`/api/teams/invitations/accept/${token}`);
      toast({
        title: "Success",
        description: "Successfully joined the team!",
      });
      setLocation('/dashboard');
    } catch (error: any) {
      // Handle 402 Payment Required specifically
      if (error.response?.status === 402) {
        toast({
          title: "No Available Seats",
          description: error.response?.data?.detail || 'All assistant seats are occupied. The team owner needs to purchase more seats.',
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || 'Failed to accept invitation',
          variant: "destructive",
        });
      }
    } finally {
      setAccepting(false);
    }
  };

  const declineInvitation = async () => {
    if (!token || !confirm('Are you sure you want to decline this invitation?')) return;
    
    try {
      await api.post(`/api/teams/invitations/decline/${token}`);
      toast({
        title: "Info",
        description: "Invitation declined",
      });
      setLocation('/dashboard');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to decline invitation',
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto p-4 bg-primary/10 rounded-full w-fit mb-4">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Team Invitation</CardTitle>
          <CardDescription>
            You've been invited to join a team on Bloom
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {expired ? (
            <>
              <div className="text-center space-y-2">
                <div className="mx-auto p-3 bg-red-100 rounded-full w-fit">
                  <Clock className="h-6 w-6 text-red-600" />
                </div>
                <p className="text-lg font-medium">Invitation Expired</p>
                <p className="text-sm text-muted-foreground">
                  This invitation has expired. Please ask {invitation.inviter_name} to send a new invitation.
                </p>
              </div>
              <Button onClick={() => setLocation('/dashboard')} className="w-full">
                Go to Dashboard
              </Button>
            </>
          ) : wrongUser ? (
            <>
              <div className="text-center space-y-4">
                <div className="mx-auto p-3 bg-yellow-100 rounded-full w-fit">
                  <X className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="space-y-2">
                  <p className="text-lg font-medium">Wrong Account</p>
                  <p className="text-sm text-muted-foreground">
                    This invitation is for <strong>{invitation.email}</strong>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    You're currently logged in as <strong>{user?.email}</strong>
                  </p>
                </div>
                <div className="pt-2 space-y-3">
                  <Button 
                    onClick={async () => {
                      // Set a flag to prevent re-rendering during logout
                      setLoading(true);
                      
                      // Clear all auth data manually to ensure complete logout
                      localStorage.removeItem('sessionId');
                      localStorage.removeItem('user');
                      
                      // Also sign out from Supabase
                      const { supabase } = await import('@/lib/supa');
                      await supabase.auth.signOut();
                      
                      // Force navigation to login page with the invitation token as a return URL
                      window.location.href = `/auth/login?return=/teams/join?token=${token}`;
                    }} 
                    className="w-full"
                  >
                    Log Out and Try Again
                  </Button>
                  <Button 
                    onClick={() => setLocation('/dashboard')} 
                    variant="outline"
                    className="w-full"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Team</p>
                    <p className="font-medium text-lg">{invitation.team_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Invited by</p>
                    <p className="font-medium">
                      {invitation.inviter_name} ({invitation.inviter_email})
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Your role</p>
                    <p className="font-medium capitalize">{invitation.role}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">What you'll get access to:</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5" />
                      <span>Shared player database with your team</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5" />
                      <span>Collaborative shortlists and recruiting boards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5" />
                      <span>Team communication and activity tracking</span>
                    </li>
                    {invitation.role === 'admin' && (
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-500 mt-0.5" />
                        <span>Admin privileges to manage team settings</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={acceptInvitation} 
                  disabled={accepting}
                  className="w-full"
                  size="lg"
                >
                  {accepting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Joining Team...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>
                <Button 
                  onClick={declineInvitation} 
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}